import {
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { useInvoiceBulkJobQuery } from '../hooks/useInvoiceBulk';
import { BULK_JOB_STATUS_LABELS, type InvoiceBulkJobRow } from '../types/invoiceBulk';

interface InvoiceBulkJobDetailDialogProps {
  jobId: string | null;
  onClose: () => void;
}

const formatDate = (value: string | null): string => (value && value.trim() ? value : '—');

/** 일괄등록 잡 상세 — 행별 성공/실패 + 실패 사유. */
export const InvoiceBulkJobDetailDialog = ({ jobId, onClose }: InvoiceBulkJobDetailDialogProps) => {
  const query = useInvoiceBulkJobQuery(jobId);

  const columns: DataTableColumn<InvoiceBulkJobRow>[] = [
    {
      key: 'rowNo',
      header: '행',
      width: 56,
      align: 'right',
      cell: (row) => (
        <span className="tabular-nums text-[13px]" style={{ color: 'var(--content-muted)' }}>
          {row.rowNo}
        </span>
      ),
    },
    {
      key: 'orderNo',
      header: '주문번호',
      cell: (row) => (
        <span className="text-[13px]" style={{ color: 'var(--content-strong)' }}>
          {row.orderNo}
          {row.productOrderNo ? (
            <span className="ml-1 text-[11px]" style={{ color: 'var(--content-muted)' }}>
              / {row.productOrderNo}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'courier',
      header: '택배사 / 송장',
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.courierCode ?? '—'} / {row.trackingNo ?? '—'}
        </span>
      ),
    },
    {
      key: 'dates',
      header: '배송일 / 완료일',
      width: 160,
      cell: (row) => (
        <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDate(row.shippedDate)} / {formatDate(row.deliveredDate)}
        </span>
      ),
    },
    {
      key: 'result',
      header: '결과',
      width: 80,
      align: 'center',
      cell: (row) =>
        row.result === 'SUCCESS' ? (
          <StatusBadge label="성공" variant="success" size="sm" />
        ) : (
          <StatusBadge label="실패" variant="danger" size="sm" />
        ),
    },
    {
      key: 'failReason',
      header: '실패 사유',
      cell: (row) => (
        <span className="text-[12px]" style={{ color: 'var(--signal-danger)' }}>
          {row.result === 'FAIL' ? row.failReason ?? '사유 미상' : ''}
        </span>
      ),
    },
  ];

  return (
    <Dialog open={jobId !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[860px]">
        <DialogHeader>
          <DialogTitle>일괄등록 상세</DialogTitle>
          <DialogDescription>
            {query.data
              ? `${query.data.job.fileName} · 총 ${query.data.job.totalCount}건 (성공 ${query.data.job.successCount} / 실패 ${query.data.job.failCount} / 상태변경 실패 ${query.data.job.statusChangeFailCount})`
              : '등록 결과를 불러옵니다.'}
          </DialogDescription>
        </DialogHeader>

        {query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[36px] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-8 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>상세 내역을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-auto">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
              <StatusBadge
                label={BULK_JOB_STATUS_LABELS[query.data.job.status]}
                variant={
                  query.data.job.status === 'FAILED'
                    ? 'danger'
                    : query.data.job.status === 'COMPLETED'
                      ? 'success'
                      : 'warning'
                }
                size="sm"
              />
              <span style={{ color: 'var(--content-muted)' }}>{query.data.job.createdAt}</span>
            </div>
            <DataTable
              columns={columns}
              rows={[...query.data.rows]}
              rowKey={(row) => row.rowNo}
              emptyState="처리된 행이 없습니다."
              stickyHeader
              ariaLabel="일괄등록 행 목록"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
